import {utils} from '@wallaby-ui/core';
import {differenceInDays, format, isValid} from 'date-fns';
import {isEmpty, isEqual, isNil} from 'lodash';
import React, {useState, useEffect, useCallback} from 'react';
import {Placement} from 'react-bootstrap/esm/types';
import {
  DynamicFilter,
  Filter,
  FILTER_TYPE_DATE_RANGE,
  FILTER_TYPE_DATE_ONLY_RANGE,
  ShortcutEnum,
  DateRangeFilter,
} from 'src/js/lib/filtering';
import {FilterNode as FilterNodeModel} from 'src/js/model/filters_config';

import {FilterNodeTypeDateRange} from './FilterNodeTypeDateRange';
import {FilterNodeTypeWrapper} from './FilterNodeTypeWrapper';

export interface FilterNodeTypeDateRangeContainerOwnProps {
  dateOnly?: boolean;
  node: FilterNodeModel;
  changeFilter: (filter: DateRangeFilter) => void;
  inputRef: () => void;
  isMui?: boolean;
  isOpened?: boolean;
  removeFilter: (filter: DateRangeFilter) => void;
  selectedFilter?: DateRangeFilter;
  setFocus: () => void;
  toggleOpened: () => void;
  isDefaultValue?: boolean;
  placement?: Placement;
  fullWidth?: boolean;
  enableFuture?: boolean;
  showShortcuts?: boolean;
  isLabResults?: boolean;
  isMedications?: boolean;
}

type Props = FilterNodeTypeDateRangeContainerOwnProps;

export const FilterNodeTypeDateRangeContainer: React.FC<Props> = (props) => {
  const {
    changeFilter,
    dateOnly = false,
    inputRef,
    isMui = false,
    isOpened = false,
    node,
    removeFilter,
    selectedFilter,
    setFocus,
    toggleOpened,
    isDefaultValue,
    placement,
    fullWidth = false,
    enableFuture = false,
    showShortcuts,
    isLabResults,
    isMedications = false,
  } = props;

  const [suppressFocus, setSuppressFocus] = useState<boolean>(false);
  // TODO: check if still necessary
  const [prevIsOpened, setPrevIsOpened] = useState<boolean>(false);
  const [start, setStart] = useState<Date | null>(null);
  const [end, setEnd] = useState<Date | null>(null);
  const [prevStart, setPrevStart] = useState<Date | null>(null);
  const [prevEnd, setPrevEnd] = useState<Date | null>(null);

  const setValuesFromSelectedFilter = useCallback(() => {
    setStart(selectedFilter ? selectedFilter.getStartDate() : null);
    setEnd(selectedFilter ? selectedFilter.getEndDate() : null);
    setPrevStart(selectedFilter ? selectedFilter.getStartDate() : null);
    setPrevEnd(selectedFilter ? selectedFilter.getEndDate() : null);
  }, [selectedFilter]);

  useEffect(() => {
    if (!selectedFilter || !isEqual(selectedFilter.getStartDate(), prevStart) || !isEqual(selectedFilter.getEndDate(), prevEnd)) {
      setValuesFromSelectedFilter();
    }
  }, [selectedFilter, prevStart, prevEnd, setValuesFromSelectedFilter]);

  useEffect(() => {
    if (!isEqual(isOpened, prevIsOpened)) {
      setSuppressFocus(!isOpened || prevIsOpened);
      setPrevIsOpened(isOpened);
    }
  }, [isOpened, prevIsOpened]);

  const getStartDate = useCallback(() => {
    return isNil(start) ? null : new Date(start);
  }, [start]);

  const getEndDate = useCallback(() => {
    return isNil(end) ? null : new Date(end);
  }, [end]);

  const getShortcutValue = useCallback(() => {
    if (selectedFilter && 'getShortcut' in selectedFilter) {
      return selectedFilter.getShortcut() as ShortcutEnum | null;
    }
    return undefined;
  }, [selectedFilter]);

  const getFilter = useCallback(
    (value: any = {}) => {
      const filterType = dateOnly ? FILTER_TYPE_DATE_ONLY_RANGE : FILTER_TYPE_DATE_RANGE;
      const DateRangeFilter = DynamicFilter.getFilterClass(filterType);
      return new DateRangeFilter({
        node,
        type: filterType,
        property: node.getName(),
        value,
      });
    },
    [dateOnly, node]
  );

  const handleChange = useCallback(
    (value: any) => {
      const filter: DateRangeFilter = getFilter(value);
      const [startDate, endDate] = filter.getValue();
      if (isValid(new Date(startDate)) && isValid(new Date(endDate))) {
        changeFilter(filter);
      }
    },
    [changeFilter, getFilter]
  );

  const handleChangeEnd = (value: Date | null) => {
    if (isValid(value) && value !== null) {
      setEnd(value);
    }
    setSuppressFocus(true);
  };

  const handleChangeStart = (value: Date | null) => {
    if (isValid(value) && value !== null) {
      setStart(value);
    }
    const currentEndDate = getEndDate();
    if (currentEndDate && value !== null && currentEndDate < value) {
      // update end date if it is before new startDate
      handleChangeEnd(value);
    }
    setSuppressFocus(true);
  };

  const clearDates = useCallback(() => {
    setStart(null);
    setEnd(null);
    setPrevStart(null);
    setPrevEnd(null);
  }, []);

  // Discard uncommitted edits and snap back to the last committed range.
  // Used when the popper closes without a complete range being applied,
  // so abandoned From-only navigation does not bound the next open.
  const resetToCommitted = useCallback(() => {
    setStart(prevStart);
    setEnd(prevEnd);
  }, [prevStart, prevEnd]);

  const handleButtonSubmit = useCallback(
    (value?: [Date | null | undefined, Date | null | undefined, ShortcutEnum | null | undefined] | undefined) => {
      let currentStartDate = getStartDate();
      let currentEndDate = getEndDate();
      let currentShortcut = getShortcutValue();
      if (value && value.length >= 2) {
        // force new values from arguments
        if (value[0]) {
          [currentStartDate] = value;
        }
        if (value[1]) {
          [, currentEndDate] = value;
        }
        if (value.length === 3) {
          [, , currentShortcut] = value;
        }
      }
      if (
        currentStartDate &&
        currentEndDate &&
        isValid(currentStartDate) &&
        isValid(currentEndDate) &&
        currentEndDate < currentStartDate
      ) {
        return;
      }
      if ((dateOnly || isMui) && isValid(currentStartDate) && isValid(currentEndDate) && currentEndDate && currentStartDate) {
        currentStartDate = format(
          currentStartDate,
          dateOnly ? utils.DateUtils.ISO_DATE_ONLY_FORMAT : utils.DateUtils.ISO_DATE_TIME_FORMAT
        ) as unknown as Date;
        currentEndDate = format(
          currentEndDate,
          dateOnly ? utils.DateUtils.ISO_DATE_ONLY_FORMAT : utils.DateUtils.ISO_DATE_TIME_FORMAT
        ) as unknown as Date;
      }
      handleChange([currentStartDate, currentEndDate, currentShortcut]);
    },
    [dateOnly, getEndDate, getStartDate, handleChange, isMui, getShortcutValue]
  );

  const getDateDifference = () => {
    // the closed interval (1.1.2018 - 1.1.2018 is 1 day)
    return !isNil(start) && !isNil(end) ? differenceInDays(utils.DateUtils.resetTime(end), utils.DateUtils.resetTime(start)) + 1 : 0;
  };

  const isDisabled = () => {
    return isNil(start) || isNil(end) || end < start;
  };

  const filter = getFilter();
  const currentStartDate = getStartDate();
  const currentEndDate = getEndDate();
  const currentShortcut = getShortcutValue();
  const dateDifference = getDateDifference();

  return (
    <FilterNodeTypeWrapper
      filter={filter}
      isOpened={isOpened}
      node={node}
      removeFilter={removeFilter}
      setFocus={setFocus}
      selectedFilter={selectedFilter}
      suppressFocus={suppressFocus}
      toggleOpened={toggleOpened}
      isMui={isMui}
    >
      <FilterNodeTypeDateRange
        dateDifference={dateDifference}
        handleButtonSubmit={handleButtonSubmit}
        handleChangeStart={handleChangeStart}
        handleChangeEnd={handleChangeEnd}
        clearDates={clearDates}
        resetToCommitted={resetToCommitted}
        inputRef={inputRef}
        isDisabled={isDisabled()}
        isEmpty={isEmpty(selectedFilter)}
        isMui={isMui}
        isOpened={isOpened}
        label={node.getLabel()}
        startDate={currentStartDate}
        endDate={currentEndDate}
        shortcut={currentShortcut}
        removeFilter={removeFilter}
        selectedFilter={selectedFilter}
        isDefaultValue={isDefaultValue}
        placement={placement}
        fullWidth={fullWidth}
        enableFuture={enableFuture}
        showShortcuts={showShortcuts}
        isLabResults={isLabResults}
        isMedications={isMedications}
      />
    </FilterNodeTypeWrapper>
  );
};

FilterNodeTypeDateRangeContainer.displayName = 'FilterNodeTypeDateRangeContainer';
